import React, { type ComponentType, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "utils/styling";

import { CenterLoading } from "./Loading";

type ContainerProps = {
  children?: ReactNode;
  className?: string;
  height?: string;
};

const Container = ({ children, className, height }: ContainerProps) => (
  <div className={cn("relative flex items-center justify-center", className)} style={{ height }}>
    {children}
  </div>
);

type EmptyComponentProps = {
  className?: string;
  height?: string;
};

const DefaultEmptyComponent = ({ className, height }: EmptyComponentProps) => {
  const { t } = useTranslation();
  return (
    <Container className={className} height={height}>
      <p className=" -mt-2 font-ds-body-semibold-lg ">{t("Modernization.goals.noGoals")}</p>
    </Container>
  );
};

type LoadingOrEmptyProps = {
  className?: string;
  emptyComponent?: ComponentType<EmptyComponentProps>;
  height?: string;
  loadingText?: string | undefined;
  showEmpty?: boolean;
  showLoading?: boolean;
};

export const LoadingOrEmptyComponent = ({
  className,
  emptyComponent,
  height = "10rem",
  loadingText,
  showEmpty,
  showLoading,
}: LoadingOrEmptyProps) => {
  const EmptyComponent = emptyComponent ?? (() => <DefaultEmptyComponent height={height} />);
  if (showLoading)
    return (
      <Container className={className} height={height}>
        <CenterLoading label={loadingText} />
      </Container>
    );
  if (showEmpty) return <EmptyComponent className={className} />;
  return null;
};

type LoadingOrEmptyWrapperProps = LoadingOrEmptyProps & {
  children?: ReactNode | undefined;
};

export const LoadingOrEmptyWrapper = ({ children, ...props }: LoadingOrEmptyWrapperProps) => {
  const { showEmpty, showLoading } = props;
  if (showEmpty || showLoading) return <LoadingOrEmptyComponent {...props} />;
  return <>{children}</>;
};
